import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * Hook for real-time bid notifications via WebSocket
 * @param {string} eventId - The event ID to subscribe to
 * @param {function} onNewBid - Callback when a new bid is received
 * @returns {object} - { isConnected, error, latestBid }
 */
export function useBidSocket(eventId, onNewBid) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [latestBid, setLatestBid] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (!eventId || !WS_URL) return;

    try {
      const wsUrl = `${WS_URL}/api/ws/bids/${eventId}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        console.log(`WebSocket connected for event ${eventId}`);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        // Handle pong responses
        if (event.data === 'pong') return;
        
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_bid') {
            setLatestBid(data);
            if (onNewBid) {
              onNewBid(data);
            }
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        setError('Connection error');
      };

      ws.onclose = (e) => {
        setIsConnected(false);
        console.log(`WebSocket closed for event ${eventId}`, e.code, e.reason);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after 5 seconds (unless intentionally closed)
        if (e.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connect();
          }, 5000);
        }
      };
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Failed to connect');
    }
  }, [eventId, onNewBid]);

  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [connect]);

  return { isConnected, error, latestBid };
}

export default useBidSocket;
